import { useEffect, useState, useContext } from "react";
import { Label, TextInput, Textarea, Button } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { useGetData } from "../../../hooks/useGetData";
import { EvaluadoInterface } from "../../../interfaces/EvaluadoInterface";
import { Loading } from "../../../components/Loading/Loading";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../../../global/global";
import { DatosGeneralesPrint } from "./DatosGeneralesPrint";
import Header from "../../../components/HeaderForms/HeaderForms";
import { ThemeContext } from "../../../components/ThemeContext/ThemeContext";

export const DatosGenerales = () => {
  const tituloFormulario = "DATOS GENERALES";
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { reset, control } = useForm();
  const [edad, setEdad] = useState<number | null>(null);
  const [numeroHijos, setNumeroHijos] = useState(0);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const { isDarkMode } = useContext(ThemeContext);
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const calcularEdad = (fechaNacimiento: string) => {
    const fechaNac = new Date(fechaNacimiento);
    const hoy = new Date();
    let edadCalculada = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
      edadCalculada--;
    }
    return edadCalculada;
  };

  const normalizeString = (str: string) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const nombreCompleto = normalizeString(
    `${queryEvaluado.data?.nombre}_${queryEvaluado.data?.apellido_paterno}_${queryEvaluado.data?.apellido_materno}`.replace(
      /\s/g,
      "_"
    )
  );

  const handlePrint = () => {
    const printContent = document.getElementById("printableContent");

    if (printContent) {
      const printWindow = window.open("", "", "width=800,height=600");

      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Vista Previa de Impresión</title>
              <style>
                @media print {
                  body {
                    margin: 20px;
                    font-family: Arial, sans-serif;
                  }
                }
              </style>
            </head>
            <body>${printContent.innerHTML}</body>
          </html>
        `);

        const styles = document.styleSheets;
        for (let i = 0; i < styles.length; i++) {
          try {
            if (styles[i].cssRules) {
              const styleTag = printWindow.document.createElement("style");
              for (let j = 0; j < styles[i].cssRules.length; j++) {
                styleTag.appendChild(
                  printWindow.document.createTextNode(
                    styles[i].cssRules[j].cssText
                  )
                );
              }
              printWindow.document.head.appendChild(styleTag);
            }
          } catch (e) {
            console.warn("No se pudo copiar una hoja de estilo:", e);
          }
        }

        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 500);
        };
      } else {
        alert("No se pudo abrir la ventana de impresión.");
      }
    }
  };

  useEffect(() => {
    if (queryEvaluado.data) {
      const nuevaEdad = calcularEdad(queryEvaluado.data.fecha_nacimiento || "");
      setEdad(nuevaEdad);
      reset(queryEvaluado.data);
    }
  }, [queryEvaluado.data, reset]);

  useEffect(() => {
    if (evaluadosList_id) {
      const token = localStorage.getItem("token");
      fetch(`${API}/evaluados/${evaluadosList_id}/photo/${nombreCompleto}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.url) {
            setPhotoUrl(data.url);
            console.log("URL de la foto:", data.url);
          } else {
            setPhotoUrl(null);
          }
        })
        .catch((error) => {
          console.error("Error al obtener la URL de la foto:", error);
          setPhotoUrl(null);
        });
    }
  }, [evaluadosList_id, nombreCompleto]);

  useEffect(() => {
    if (queryEvaluado.data) {
      reset({
        nombre: queryEvaluado.data.nombre || "",
        apellido_paterno: queryEvaluado.data.apellido_paterno || "",
        apellido_materno: queryEvaluado.data.apellido_materno || "",
        correo: queryEvaluado.data.correo || "",
        telefono: queryEvaluado.data.telefono || "",
        empresa_id: String(queryEvaluado.data.empresa_id.id),
        puesto: queryEvaluado.data.puesto || "",
        fecha_ingreso: queryEvaluado.data.fecha_ingreso || "",
        antiguedad: queryEvaluado.data.antiguedad || "",
        fecha_nacimiento: queryEvaluado.data.fecha_nacimiento || "",
        genero: queryEvaluado.data.genero || "",
        lugar_nacimiento: queryEvaluado.data.lugar_nacimiento || "",
        nacionalidad: queryEvaluado.data.nacionalidad || "",
        curp: queryEvaluado.data.curp || "",
        rfc: queryEvaluado.data.rfc || "",
        escolaridad: queryEvaluado.data.escolaridad || "",
        calle: queryEvaluado.data.calle || "",
        numero_exterior: queryEvaluado.data.numero_exterior || "",
        numero_interior: queryEvaluado.data.numero_interior || "",
        referencia: queryEvaluado.data.referencia || "",
        colonia: queryEvaluado.data.colonia || "",
        codigo_postal: queryEvaluado.data.codigo_postal || "",
        municipio: queryEvaluado.data.municipio || "",
        estado: queryEvaluado.data.estado || "",
        pais: queryEvaluado.data.pais || "",
        anios_domicilio_viviendo:
          queryEvaluado.data.anios_domicilio_viviendo || "",
        personas_vivienda: queryEvaluado.data.personas_vivienda || "",
        estado_civil: queryEvaluado.data.estado_civil || "",
        vive_con: queryEvaluado.data.vive_con || "",
        tiempo_casado: queryEvaluado.data.tiempo_casado || "",
        nombre_conyuge: queryEvaluado.data.nombre_conyuge || "",
        conyuge_oficio: queryEvaluado.data.conyuge_oficio || "",
        cuantos_hijos: queryEvaluado.data.cuantos_hijos || "",
        nombre_hijo1: queryEvaluado.data.nombre_hijo1 || "",
        edad_hijo1: queryEvaluado.data.edad_hijo1 || "",
        nombre_hijo2: queryEvaluado.data.nombre_hijo2 || "",
        edad_hijo2: queryEvaluado.data.edad_hijo2 || "",
        nombre_hijo3: queryEvaluado.data.nombre_hijo3 || "",
        edad_hijo3: queryEvaluado.data.edad_hijo3 || "",
        nombre_hijo4: queryEvaluado.data.nombre_hijo4 || "",
        edad_hijo4: queryEvaluado.data.edad_hijo4 || "",
        nombre_hijo5: queryEvaluado.data.nombre_hijo5 || "",
        telefono_recados: queryEvaluado.data.telefono_recados || "",
        empresa_labora: queryEvaluado.data.empresa_labora || "",
      });

      setNumeroHijos(Number(queryEvaluado.data.cuantos_hijos) || 0);
    }
  }, [queryEvaluado.data, reset, uuid]);

  console.log("Telefono de recados: ", queryEvaluado.data?.telefono_recados);
  console.log("Telefono: ", queryEvaluado.data?.telefono);

  if (queryEvaluado.isLoading) {
    return <Loading />;
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div
        className={`max-w-4xl mx-auto shadow-lg rounded-lg overflow-hidden mt-4 mb-4 relative ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        {/* Marca de agua */}
        <div className="absolute inset-0 flex justify-center items-center pointer-events-none z-0">
          <div
            className={`text-9xl font-bold transform rotate-[-45deg] ${
              isDarkMode ? "text-gray-600" : "text-blue-500"
            }`}
            style={{ opacity: 0.2 }}
          >
            CONFIDENCIAL
          </div>
        </div>

        {/* Header */}
        <Header titulo={tituloFormulario} />

        {/* Contenido del formulario */}
        <div className={`p-6 grid ${isDarkMode ? "text-white" : "text-black"}`}>
          {/* Left Column - Form Fields */}
          <div className="col-span-2 space-y-4">
            {/* Grupo de Empresa, Puesto y Fecha de Ingreso */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label
                  htmlFor="puesto"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  FECHA DE EVALUACION:
                </Label>
                <TextInput
                  id="puesto"
                  name="puesto"
                  value={
                    queryEvaluado.data?.fecha_evaluacione
                      ? (() => {
                          const date = new Date(
                            queryEvaluado.data.fecha_evaluacione
                          );
                          date.setDate(date.getDate() + 1);
                          return date.toLocaleDateString("es-ES", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          });
                        })()
                      : "Fecha no disponible"
                  }
                  disabled={true}
                  className={`flex-1 ml-4 ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>

              <div className="flex justify-center items-center">
                <Label
                  htmlFor="empresa"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  EMPRESA A LA QUE SE POSTULA Y EMPRESA PARA LA QUE LABORA:
                </Label>
                <TextInput
                  id="empresa"
                  name="empresa"
                  value={`${queryEvaluado.data?.empresa_id?.nombre || ""} ${
                    queryEvaluado.data?.empresa_labora
                      ? "y " + queryEvaluado.data.empresa_labora
                      : ""
                  }`}
                  disabled
                  className={`flex-1 ml-4 ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>

              <div className="flex justify-center items-center">
                <Label
                  htmlFor="empresa"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  ¿SE POSTULA O EVALÚA?
                </Label>
                <TextInput
                  id="sepostula_evalua"
                  name="sepostula_evalua"
                  value={`${queryEvaluado.data?.sepostula_evalua || ""} `}
                  disabled
                  className={`flex-1 ml-4 ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>

              <div className="flex justify-between items-center">
                <Label
                  htmlFor="puesto"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  PUESTO:
                </Label>
                <TextInput
                  id="puesto"
                  name="puesto"
                  value={queryEvaluado.data?.puesto || ""}
                  disabled
                  className={`flex-1 ml-4 ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>

              <div className="flex justify-between items-center">
                <Label
                  htmlFor="fecha"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  FECHA DE INGRESO Y ANTIGÜEDAD:
                </Label>
                <TextInput
                  id="fecha"
                  type="text"
                  value={
                    queryEvaluado.data?.fecha_ingreso ||
                    queryEvaluado.data?.antiguedad
                      ? `${
                          queryEvaluado.data?.fecha_ingreso
                            ? `Ingresó el ${new Date(
                                queryEvaluado.data.fecha_ingreso
                              ).toLocaleDateString("es-ES")}`
                            : ""
                        }${
                          queryEvaluado.data?.fecha_ingreso &&
                          queryEvaluado.data?.antiguedad
                            ? " y tiene "
                            : queryEvaluado.data?.antiguedad
                            ? "Tiene "
                            : ""
                        }${
                          queryEvaluado.data?.antiguedad
                            ? `${queryEvaluado.data.antiguedad} años de antigüedad`
                            : ""
                        }`.trim()
                      : ""
                  }
                  disabled
                  className={`flex-1 ml-4 ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
            </div>

            {/* Barra separadora */}
            <div
              className={`text-white p-2 rounded-t-lg z-10 relative ${
                isDarkMode ? "bg-blue-800" : "bg-customBlue"
              }`}
            >
              <h2 className="font-bold text-lg">DATOS GENERALES</h2>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* NOMBRE COMPLETO */}
              <div className="flex flex-col col-span-2">
                <div className="flex flex-col mb-4">
                  <Label
                    htmlFor="nombre"
                    className={`font-semibold text-left ${
                      isDarkMode ? "text-gray-300" : "text-gray-800"
                    }`}
                  >
                    NOMBRE COMPLETO:
                  </Label>
                  <TextInput
                    id="nombre"
                    name="nombre"
                    value={`${queryEvaluado.data?.nombre || ""} ${
                      queryEvaluado.data?.apellido_paterno || ""
                    } ${queryEvaluado.data?.apellido_materno || ""}`}
                    disabled
                    className={`w-full ${
                      isDarkMode
                        ? "bg-gray-700 text-white"
                        : "bg-white text-gray-700"
                    }`}
                  />
                </div>

                {/* Fecha de Nacimiento */}
                <div className="flex flex-col mb-4">
                  <Label
                    htmlFor="fechaNac"
                    className={`font-semibold text-left ${
                      isDarkMode ? "text-gray-300" : "text-gray-800"
                    }`}
                  >
                    FECHA DE NACIMIENTO:
                  </Label>
                  <TextInput
                    id="fechaNac"
                    value={queryEvaluado.data?.fecha_nacimiento || ""}
                    disabled
                    type="date"
                    className={`w-full ${
                      isDarkMode
                        ? "bg-gray-700 text-white"
                        : "bg-white text-gray-700"
                    }`}
                  />
                </div>

                {/* Lugar de Nacimiento */}
                <div className="flex flex-col mb-4">
                  <Label
                    htmlFor="lugarNac"
                    className={`font-semibold text-left ${
                      isDarkMode ? "text-gray-300" : "text-gray-800"
                    }`}
                  >
                    LUGAR DE NACIMIENTO:
                  </Label>
                  <TextInput
                    id="lugarNac"
                    value={queryEvaluado.data?.lugar_nacimiento || ""}
                    disabled
                    className={`w-full ${
                      isDarkMode
                        ? "bg-gray-700 text-white"
                        : "bg-white text-gray-700"
                    }`}
                  />
                </div>

                {/* Nacionalidad */}
                <div className="flex flex-col mb-4">
                  <Label
                    htmlFor="nacionalidad"
                    className={`font-semibold text-left ${
                      isDarkMode ? "text-gray-300" : "text-gray-800"
                    }`}
                  >
                    NACIONALIDAD:
                  </Label>
                  <TextInput
                    id="nacionalidad"
                    value={queryEvaluado.data?.nacionalidad || ""}
                    disabled
                    className={`w-full ${
                      isDarkMode
                        ? "bg-gray-700 text-white"
                        : "bg-white text-gray-700"
                    }`}
                  />
                </div>
              </div>

              {/* FOTO */}
              <div
                className={`w-64 h-64 border rounded-md overflow-hidden bg-gray-100 flex items-center justify-center mt-4 relative z-10 ${
                  isDarkMode ? "border-gray-600" : "border-gray-300"
                }`}
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt="Foto de evaluado"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span
                    className={`text-gray-500 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Sin foto disponible
                  </span>
                )}
              </div>
            </div>

            {/* CURP y RFC */}
            <div className="flex space-x-4 w-full">
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="curp"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  CURP:
                </Label>
                <TextInput
                  id="curp"
                  value={queryEvaluado.data?.curp || ""}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="rfc"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  RFC:
                </Label>
                <TextInput
                  id="rfc"
                  value={queryEvaluado.data?.rfc || ""}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
            </div>

            {/* ULTIMO GRADO DE ESTUDIOS y DOMICILIO PARTICULAR en una misma fila */}
            <div className="flex space-x-4 w-full">
              <div className="flex flex-col flex-[1]">
                <Label
                  htmlFor="gradoEstudios"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  ULTIMO GRADO DE ESTUDIOS:
                </Label>
                <TextInput
                  id="gradoEstudios"
                  value={queryEvaluado.data?.escolaridad || ""}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
              <div className="flex flex-col flex-[3]">
                <Label
                  htmlFor="domicilio"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  DOMICILIO PARTICULAR:
                </Label>
                <TextInput
                  id="domicilio"
                  value={`${queryEvaluado.data?.calle || ""}, ${
                    queryEvaluado.data?.numero_exterior || ""
                  }, ${queryEvaluado.data?.numero_interior || ""}, ${
                    queryEvaluado.data?.colonia || ""
                  }, ${queryEvaluado.data?.codigo_postal || ""}, ${
                    queryEvaluado.data?.municipio || ""
                  }, ${queryEvaluado.data?.estado || ""}, ${
                    queryEvaluado.data?.pais || ""
                  }`}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
            </div>

            {/* CIUDAD DEL DOMICILIO DONDE VIVE Y CUANTOS AÑOS TIENE VIVIENDO EN ESE DOMICILIO */}
            <div className="flex space-x-4 w-full">
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="Ciudad"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  CIUDAD DEL DOMICILIO DONDE VIVE:
                </Label>
                <TextInput
                  id="Ciudad"
                  value={queryEvaluado.data?.estado || ""}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="Años viviendo"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  CUANTOS AÑOS TIENE VIVIENDO EN ESE DOMICILIO:
                </Label>
                <Textarea
                  id="Años viviendo"
                  value={queryEvaluado.data?.anios_domicilio_viviendo || ""}
                  rows={5}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
            </div>

            {/* CON QUIEN VIVE Y TELEFONO Y TELEFONO PARA RECADOS */}
            <div className="flex space-x-4 w-full">
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="Con quien"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  CON QUIEN VIVE:
                </Label>
                <TextInput
                  id="Con quien"
                  value={queryEvaluado.data?.vive_con}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="telefono"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  TELEFONO Y TELEFONO PARA RECADOS:
                </Label>
                <Textarea
                  id="telefono"
                  value={`(${queryEvaluado.data?.telefono || ""})  (${
                    queryEvaluado.data?.telefono_recados || ""
                  })`}
                  rows={5}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
            </div>

            {/* CORREO ELECTRÓNICO Y ESTADO CIVIL */}
            <div className="flex space-x-4 w-full">
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="correo"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  CORREO ELECTRÓNICO:
                </Label>
                <TextInput
                  id="correo"
                  value={queryEvaluado.data?.correo}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="estado civil"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  ESTADO CIVIL:
                </Label>
                <TextInput
                  id="estado civil"
                  value={
                    queryEvaluado.data?.estado_civil
                      ? queryEvaluado.data.estado_civil
                          .charAt(0)
                          .toUpperCase() +
                        queryEvaluado.data.estado_civil.slice(1).toLowerCase()
                      : ""
                  }
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
            </div>

            {/* TIEMPO DE CASADO(A) Y/0 CONCUBINATO Y A QUE SE DEDICA SU ESPOSA(O) Y/O CONCUBINO */}
            <div className="flex space-x-4 w-full">
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="tiempo casado"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  TIEMPO DE CASADO(A) Y/0 CONCUBINATO:
                </Label>
                <TextInput
                  id="tiempo casado"
                  value={queryEvaluado.data?.tiempo_casado}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="ocupacion conyugue"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  A QUE SE DEDICA SU ESPOSA(O) Y/O CONCUBINA(O):
                </Label>
                <TextInput
                  id="ocupacion conyugue"
                  value={queryEvaluado.data?.conyuge_oficio}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
            </div>

            {/* NOMBRE DE ESPOSA(O) Y/O CONCUBINO Y CUANTOS HIJOS TIENE */}
            <div className="flex space-x-4 w-full">
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="nombre esposa"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  NOMBRE DE ESPOSA(O) Y/O CONCUBINA(O):
                </Label>
                <TextInput
                  id="nombre esposa"
                  value={queryEvaluado.data?.nombre_conyuge}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="cuantos_hijos"
                  value="Cuantos hijos tiene"
                  className={`${isDarkMode ? "text-gray-300" : "text-gray-800"}`}
                />
                <Controller
                  name="cuantos_hijos"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Se requiere un número de hijos",
                    max: { value: 4, message: "Máximo 4 hijos permitidos" },
                    min: { value: 0, message: "Mínimo 0 hijos" },
                    validate: (value) => {
                      if (value < 0) {
                        return "No se permiten números negativos";
                      }
                      if (value > 4) {
                        return "Máximo 4 hijos permitidos";
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      id="cuantos_hijos"
                      type="number"
                      placeholder="0"
                      style={isDarkMode ? {
                        backgroundColor: '#374151',
                        color: '#e5e7eb',
                        borderColor: '#4b5563'
                      } : {}}
                      {...field}
                      min={0}
                      max={4}
                      disabled={true}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        const clampedValue = Math.min(Math.max(value, 0), 4);
                        field.onChange(clampedValue);
                        setNumeroHijos(clampedValue);
                      }}
                      color={fieldState.error ? "failure" : "default"}
                      helperText={fieldState.error?.message}
                      className={`${
                        isDarkMode
                          ? "bg-gray-700 text-white"
                          : "bg-white text-gray-700"
                      }`}
                    />
                  )}
                />
              </div>
            </div>

            {/* NOMBRE Y EDADES DE LOS HIJOS */}
            <div className="flex space-x-4 w-full">
              <div className="flex flex-col flex-1">
                <Label
                  htmlFor="nombre hijos"
                  className={`font-semibold ${
                    isDarkMode ? "text-gray-300" : "text-gray-800"
                  }`}
                >
                  NOMBRE Y EDADES DE LOS HIJOS:
                </Label>
                <TextInput
                  id="nombre hijos"
                  value={Array.from({ length: numeroHijos }, (_, index) => {
                    const nombreKey = `nombre_hijo${
                      index + 1
                    }` as keyof EvaluadoInterface;
                    const edadKey = `edad_hijo${
                      index + 1
                    }` as keyof EvaluadoInterface;
                    const nombre = queryEvaluado.data?.[nombreKey] || "";
                    const edad = queryEvaluado.data?.[edadKey] || "";
                    return `${nombre}${nombre && edad ? `, ${edad} años` : ""}`;
                  }).join(", ")}
                  disabled
                  className={`w-full ${
                    isDarkMode
                      ? "bg-gray-700 text-white"
                      : "bg-white text-gray-700"
                  }`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Botones */}
        <div className="mt-4 mb-4 px-4">
          <div className="flex justify-between">
            <Button
              color="red"
              onClick={() => {
                window.scrollTo(0, 0);
                navigate(-1);
              }}
              className={`${
                isDarkMode ? "bg-red-600 hover:bg-red-700" : "bg-red-500 hover:bg-red-600 text-white"
              }`}
            >
              Regresar
            </Button>
            <Button
              color="blue"
              onClick={handlePrint}
              className={`${
                isDarkMode ? "bg-blue-600 hover:bg-blue-700" : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              Imprimir
            </Button>
          </div>
        </div>

        {/* Imprimir */}
        <div id="printableContent" style={{ display: "none" }}>
          <DatosGeneralesPrint />
        </div>
      </div>
    </div>
  );
};